import {
  Box,
  Checkbox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { Channel } from 'lib-shared-types-channels';
import { useEffect } from 'react';
import { useSetState } from 'react-use';

import { bansApi } from 'hooks/useBans/api';
import {
  BanUserPayload,
  BanUserResponse,
  isIpAddress,
} from 'hooks/useBans/types';
import { useNotifications } from 'hooks/useNotifications';
import { channelAttributes } from 'utils/channels';

import styles from './banUserModal.module.scss';

const { banUser, banIp, getUserBans } = bansApi;

/** The interface of the event that is dispatched when the modal is closed. */
export interface BanUserModalOnCloseEvent {
  /** Whether or not the modal was canceled. */
  canceled?: boolean;
  /** The response payload from a successful ban. */
  response?: BanUserResponse;
}

interface BanUserModalProps {
  user: User;
  channel: Channel;
  csid?: number;
  ipAddress?: string;
  isOpen: boolean;
  onClose: (event: BanUserModalOnCloseEvent) => void;
}

interface BanUserModalState {
  isBanning: boolean;
  reason?: string;
  shouldBanIp: boolean;
  ban_period?: BanUserPayload['ban_period'];
  numberOfbannedUsersOnIpAddress?: number;
}

export const BanUserModal = (props: BanUserModalProps): JSX.Element => {
  const [state, setState] = useSetState<BanUserModalState>({
    isBanning: false,
    shouldBanIp: false,
  });

  const notify = useNotifications();

  useEffect(() => {
    const fetchData = async () => {
      const { ipAddress } = props;

      if (ipAddress && isIpAddress(ipAddress)) {
        const {
          data: { number_of_bans },
        } = await getUserBans({
          params: {
            ip_address: ipAddress,
            active_state: 'active',
            // slight hack - we only need the number of bans, not the actual bans objects
            limit: 0,
          },
        });

        setState({
          numberOfbannedUsersOnIpAddress: number_of_bans,
        });
      }
    };

    fetchData();
  }, []);

  const handleClose = (event: BanUserModalOnCloseEvent) => {
    props.onClose(event);
  };

  const handleConfirm = async () => {
    const { reason, ban_period, shouldBanIp } = state;
    const { channel, csid, user, ipAddress } = props;

    ampli.bansBanUserClicked({
      ipChecked: shouldBanIp,
      channel,
    });

    if (user.type === 'member') {
      const notificationOptions = {
        title: 'Can not ban agent',
        text: `It's not possible to ban an agent.`,
        icon: 'exclamation',
        type: 'warning',
      };

      notify(notificationOptions);
      return;
    }

    try {
      setState({ isBanning: true });
      const response = await banUser(user.id, {
        channel,
        csid,
        ban_period,
        reason,
      });
      if (shouldBanIp && ipAddress && isIpAddress(ipAddress)) {
        await banIp(ipAddress, {
          channel,
          csid,
          ban_period,
          reason,
        });
      }

      const notificationPayload = {
        title: 'User banned',
        text: `User has been banned`,
        icon: 'i-done',
        className: 'success',
        time: 5000,
      };

      notify(notificationPayload);
      setState({ isBanning: false });
      handleClose({ response });
    } catch (error) {
      setState({ isBanning: false });
      const notificationPayload = {
        title: 'An error occurred',
        text: `An error occurred when trying to ban user`,
        icon: 'i-exclamation',
        className: 'danger',
      };

      notify(notificationPayload);
      throw new Error('Failed to ban user');
    }
  };

  const handleCancel = () => {
    handleClose({ canceled: true });
  };

  const onBanIpChangeHandler = () => {
    setState(({ shouldBanIp }) => ({
      shouldBanIp: !shouldBanIp,
    }));
  };

  const { isOpen, user, channel, csid, ipAddress: ip } = props;
  const { name, email } = user;
  const { shouldBanIp, numberOfbannedUsersOnIpAddress } = state;
  const channelName = channelAttributes[channel]?.name;
  const userIdentity = `${name || ''} ${email || ''}`.trim();
  const ipAddressMessage =
    numberOfbannedUsersOnIpAddress && numberOfbannedUsersOnIpAddress > 0 ? (
      <Box>
        {numberOfbannedUsersOnIpAddress} other{' '}
        {numberOfbannedUsersOnIpAddress === 1 ? 'user has' : 'users have'}{' '}
        already been banned from this IP address <strong>{ip}</strong>
      </Box>
    ) : (
      false
    );

  const withCsidMessage = (
    <Text.Paragraph>
      Are you sure you want to ban user <strong>{userIdentity}</strong>? User
      will be unable to reply.
      {ipAddressMessage}
      {ip && (
        <Box className={styles.ipBanContainer}>
          <Box as="span" className={styles.checkboxContainer}>
            <Checkbox
              name="shouldBanIp"
              onChange={onBanIpChangeHandler}
              checked={shouldBanIp}
            />
          </Box>
          <Text.Paragraph as="span">
            Also ban user IP address: <strong>{ip}</strong>
          </Text.Paragraph>
        </Box>
      )}
    </Text.Paragraph>
  );
  const withoutCsidMessage = (
    <Text.Paragraph>
      Are you sure you want to ban user <strong>{name}</strong> on{' '}
      <strong>{channelName}</strong> channel?
      {ipAddressMessage}
    </Text.Paragraph>
  );
  const message = csid ? withCsidMessage : withoutCsidMessage;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel}>
      <ModalHead title="Are you sure?" onClose={handleCancel} />
      <ModalBody>{message}</ModalBody>
      <ModalFoot
        cancelName="Cancel"
        cancelOnClick={handleCancel}
        confirmName="Ban user"
        confirmProps={{
          variant: 'danger',
          isLoading: state.isBanning,
          loadingIndicator: (
            <Text.Paragraph as="span" variant="3" wrap="nowrap" color="inherit">
              Banning...
            </Text.Paragraph>
          ),
        }}
        confirmOnClick={handleConfirm}
      ></ModalFoot>
    </Modal>
  );
};
